import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { gql, useQuery, useMutation } from '@apollo/client';
import {
  Container, Box, Typography, Paper, Tabs, Tab,
  Stack, Chip, IconButton, Button, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Alert,
  Divider,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import { AuthContext } from '../context/AuthContext';

const ME_QUERY = gql`
  query Me {
    me { id username email isSuperuser }
  }
`;

const BANK_ACCOUNTS_QUERY = gql`
  query BankAccountsProfile {
    bankAccounts {
      id bankName accountNumber accountUsername hasScraperSupport
    }
  }
`;

const BANK_NAMES_QUERY = gql`
  query BankNames {
    bankNames
  }
`;

const CREATE_BANK_ACCOUNT = gql`
  mutation CreateBankAccount(
    $bankName: String!
    $accountNumber: String
    $accountUsername: String!
    $credentialsRut: String!
    $credentialsPassword: String!
  ) {
    createBankAccount(
      bankName: $bankName
      accountNumber: $accountNumber
      accountUsername: $accountUsername
      credentialsRut: $credentialsRut
      credentialsPassword: $credentialsPassword
    ) {
      bankAccount { id bankName accountNumber accountUsername hasScraperSupport }
    }
  }
`;

const DELETE_BANK_ACCOUNT = gql`
  mutation DeleteBankAccount($id: Int!) {
    deleteBankAccount(id: $id) { ok }
  }
`;

const UPDATE_USER_EMAIL = gql`
  mutation UpdateUserEmail($email: String!) {
    updateUserEmail(email: $email) {
      ok
      user { id username email isSuperuser }
    }
  }
`;

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

function AddBankAccountDialog({ open, onClose, bankNames, onCreated }) {
  const [form, setForm] = useState({
    bankName: '',
    accountNumber: '',
    accountUsername: '',
    credentialsRut: '',
    credentialsPassword: '',
  });
  const [createBankAccount, { loading, error }] = useMutation(CREATE_BANK_ACCOUNT);

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const handleSubmit = async () => {
    try {
      await createBankAccount({ variables: { ...form } });
      onCreated();
      onClose();
      setForm({ bankName: '', accountNumber: '', accountUsername: '', credentialsRut: '', credentialsPassword: '' });
    } catch (_) {}
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Agregar cuenta bancaria</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error.message}</Alert>}

          <FormControl fullWidth size="small">
            <InputLabel>Banco</InputLabel>
            <Select value={form.bankName} label="Banco" onChange={set('bankName')}>
              {(bankNames || []).map((b) => (
                <MenuItem key={b} value={b}>{b}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Número de cuenta"
            size="small"
            fullWidth
            value={form.accountNumber}
            onChange={set('accountNumber')}
          />

          <TextField
            label="Nombre / etiqueta"
            size="small"
            fullWidth
            value={form.accountUsername}
            onChange={set('accountUsername')}
            helperText="Ej: Cuenta corriente principal"
          />

          <Divider />

          <Typography variant="caption" color="text.secondary">
            Credenciales para extracción automática de movimientos
          </Typography>

          <TextField
            label="RUT"
            size="small"
            fullWidth
            required
            value={form.credentialsRut}
            onChange={set('credentialsRut')}
            placeholder="12345678-9"
          />

          <TextField
            label="Contraseña de banca en línea"
            type="password"
            size="small"
            fullWidth
            required
            value={form.credentialsPassword}
            onChange={set('credentialsPassword')}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !form.bankName || !form.accountUsername || !form.credentialsRut || !form.credentialsPassword}
        >
          {loading ? 'Guardando...' : 'Agregar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ConfirmDeleteDialog({ open, account, onClose, onConfirm, loading }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Eliminar cuenta bancaria</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 1 }}>
          Se eliminarán también <strong>todas las transacciones</strong> asociadas a esta cuenta. Esta acción no se puede deshacer.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          ¿Eliminar <strong>{account?.bankName}</strong> ****{account?.accountNumber?.slice(-4)}?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} color="inherit">Cancelar</Button>
        <Button variant="contained" color="error" onClick={onConfirm} disabled={loading}>
          {loading ? 'Eliminando...' : 'Eliminar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Profile() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const [emailError, setEmailError] = useState('');

  const { data: meData } = useQuery(ME_QUERY);
  const { data: baData, refetch: refetchAccounts } = useQuery(BANK_ACCOUNTS_QUERY);
  const { data: bnData } = useQuery(BANK_NAMES_QUERY);
  const [deleteBankAccount, { loading: deleting }] = useMutation(DELETE_BANK_ACCOUNT);
  const [updateUserEmail, { loading: savingEmail }] = useMutation(UPDATE_USER_EMAIL, {
    refetchQueries: [{ query: ME_QUERY }],
  });

  const accounts = baData?.bankAccounts || [];
  const bankNames = bnData?.bankNames || [];

  const handleLogout = () => { logout(); navigate('/login'); };

  const handleStartEditEmail = () => {
    setEmailValue(meData?.me?.email || '');
    setEmailError('');
    setEditingEmail(true);
  };

  const handleCancelEditEmail = () => {
    setEditingEmail(false);
    setEmailValue('');
    setEmailError('');
  };

  const handleSaveEmail = async () => {
    setEmailError('');
    try {
      await updateUserEmail({ variables: { email: emailValue } });
      setEditingEmail(false);
    } catch (err) {
      setEmailError(err.message || 'Failed to update email');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteBankAccount({ variables: { id: parseInt(deleteTarget.id) } });
    setDeleteTarget(null);
    refetchAccounts();
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <AccountCircleIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4">Perfil</Typography>
      </Stack>

      <Paper sx={{ p: 0, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab label="Mi cuenta" />
            <Tab label="Cuentas bancarias" />
          </Tabs>
        </Box>

        <Box sx={{ px: 3, pb: 3 }}>
          {/* ── Tab 0: Mi Cuenta ── */}
          <TabPanel value={tab} index={0}>
            <Stack spacing={2.5} sx={{ maxWidth: 400 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Usuario</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {meData?.me?.username || user?.username || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Email</Typography>
                {editingEmail ? (
                  <Stack spacing={1} sx={{ mt: 0.5 }}>
                    {emailError && <Alert severity="error" size="small">{emailError}</Alert>}
                    <TextField
                      size="small"
                      fullWidth
                      type="email"
                      value={emailValue}
                      onChange={(e) => setEmailValue(e.target.value)}
                      disabled={savingEmail}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveEmail}
                        disabled={savingEmail || !emailValue}
                      >
                        {savingEmail ? 'Guardando...' : 'Guardar'}
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        onClick={handleCancelEditEmail}
                        disabled={savingEmail}
                      >
                        Cancelar
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <Typography variant="body1" fontWeight={600}>
                      {meData?.me?.email || '—'}
                    </Typography>
                    {meData?.me?.isSuperuser && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleStartEditEmail}
                      >
                        Editar
                      </Button>
                    )}
                  </Stack>
                )}
              </Box>
              <Box sx={{ pt: 1 }}>
                <Button
                  variant="outlined"
                  color="inherit"
                  startIcon={<LogoutIcon />}
                  onClick={handleLogout}
                >
                  Cerrar sesión
                </Button>
              </Box>
            </Stack>
          </TabPanel>

          {/* ── Tab 1: Cuentas Bancarias ── */}
          <TabPanel value={tab} index={1}>
            <Stack spacing={2}>
              {accounts.length === 0 && (
                <Typography color="text.secondary" sx={{ py: 2 }}>
                  No tienes cuentas bancarias registradas.
                </Typography>
              )}

              {accounts.map((acc) => (
                <Paper
                  key={acc.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap',
                    boxShadow: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <AccountBalanceIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography fontWeight={700} noWrap>{acc.bankName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {acc.accountUsername} · ****{acc.accountNumber.slice(-4)}
                    </Typography>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      flexShrink: 0,
                      width: { xs: '100%', sm: 'auto' },
                      mt: { xs: 0.5, sm: 0 },
                      pl: { xs: 5, sm: 0 },
                    }}
                  >
                    <Tooltip title={acc.hasScraperSupport ? 'Extracción automática disponible' : 'Sin extracción automática'} arrow>
                      <Chip
                        label={acc.hasScraperSupport ? 'Scraper activo' : 'Sin scraper'}
                        size="small"
                        color={acc.hasScraperSupport ? 'success' : 'default'}
                      />
                    </Tooltip>
                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(acc)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Paper>
              ))}

              <Box>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setAddOpen(true)}
                >
                  Agregar cuenta bancaria
                </Button>
              </Box>
            </Stack>
          </TabPanel>
        </Box>
      </Paper>

      <AddBankAccountDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        bankNames={bankNames}
        onCreated={refetchAccounts}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        account={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
      />
    </Container>
  );
}
